import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { roleApi, permissionApi } from '@/services/permissionsRolesApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ArrowLeft, Edit, Trash2, UserCog, CheckCircle2, XCircle, AlertTriangle, Plus, Minus } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Role, Permission } from '@/types/permissionsRoles';

export const RoleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [addPermissionModalOpen, setAddPermissionModalOpen] = useState(false);
  const [selectedPermissionName, setSelectedPermissionName] = useState<string>('');
  const { hasRole } = useAdminAuth();

  const canAccess = hasRole('ADMIN') || hasRole('SYSTEM');

  // Memoize the fetch function to prevent useEntity from recreating on every render
  const fetchRoleById = useCallback((roleId: string) => roleApi.getById(roleId), []);

  // Use entity hook for fetching role
  const {
    data: role,
    isLoading,
    error,
    refetch,
  } = useEntity<Role>({
    fetchFn: fetchRoleById,
    entityId: id,
    entityName: 'role',
    enabled: !!id && canAccess,
    onError: () => {
      navigate('/admin/roles');
    },
  });

  // Use permissions directly from role (API returns full Permission objects)
  useEffect(() => {
    if (role && role.permissions) {
      setPermissions(role.permissions);
    } else {
      setPermissions([]);
    }
  }, [role]);

  const fetchAllPermissionsRef = useRef(false);

  // Fetch all permissions on mount
  useEffect(() => {
    if (!canAccess || fetchAllPermissionsRef.current) {
      return;
    }
    
    fetchAllPermissionsRef.current = true;
    fetchAllPermissions().finally(() => {
      fetchAllPermissionsRef.current = false;
    });
  }, [canAccess]);

  // No need to fetch permissions separately - they come with the role
  // const fetchPermissions = async () => { ... } - removed, using role.permissions directly

  const fetchAllPermissions = async () => {
    try {
      const response = await permissionApi.list({ page: 0, size: 1000 });
      setAllPermissions(response.content.filter((p) => p.active));
    } catch (error: unknown) {
      const apiError = error as { message?: string; status?: number };
      console.error('Error fetching all permissions:', apiError);
      // Don't set empty array on 429 - keep existing permissions
      if (apiError.status !== 429) {
        setAllPermissions([]);
      }
    }
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Role ID is required');
      await roleApi.delete(id);
    },
    entityName: 'role',
    operation: 'delete',
    onSuccess: () => {
      navigate('/admin/roles');
    },
  });

  // Toggle status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async () => {
      if (!id || !role) throw new Error('Role ID and data are required');
      await roleApi.toggleStatus(id, !role.active);
    },
    entityName: 'role',
    operation: 'update',
    successMessage: (_data, _vars) => `Role ${role?.active ? 'deactivated' : 'activated'} successfully`,
    onSuccess: () => {
      refetch();
    },
  });

  // Add permission mutation
  const addPermissionMutation = useMutation({
    mutationFn: async (permissionName: string) => {
      if (!id) throw new Error('Role ID is required');
      await roleApi.addPermission(id, permissionName);
    },
    entityName: 'permission',
    operation: 'update',
    successMessage: 'Permission added successfully',
    onSuccess: () => {
      setAddPermissionModalOpen(false);
      setSelectedPermissionName('');
      refetch();
    },
  });

  // Remove permission mutation
  const removePermissionMutation = useMutation({
    mutationFn: async (permissionName: string) => {
      if (!id) throw new Error('Role ID is required');
      await roleApi.removePermission(id, permissionName);
    },
    entityName: 'permission',
    operation: 'update',
    successMessage: 'Permission removed successfully',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async () => {
    if (!id || !role) return;
    if (!window.confirm(`Are you sure you want to delete role "${role.name}"?`)) return;
    await deleteMutation.mutateAsync();
  };

  const handleToggleStatus = async () => {
    if (!id || !role) return;
    await toggleStatusMutation.mutateAsync();
  };

  const handleAddPermission = async () => {
    if (!id || !selectedPermissionName) return;
    await addPermissionMutation.mutateAsync(selectedPermissionName);
  };

  const handleRemovePermission = async (permissionName: string) => {
    if (!id) return;
    if (!window.confirm(`Remove permission "${permissionName}" from this role?`)) return;
    await removePermissionMutation.mutateAsync(permissionName);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const availablePermissions = allPermissions.filter(
    (p) => !role?.permissions?.some(perm => perm.id === p.id)
  );

  if (!canAccess) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">You do not have permission to view roles.</p>
        <Button asChild className="mt-4">
          <Link to="/admin/roles">Back to List</Link>
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading role..." />;
  }

  if (error || !role) {
    return (
      <div className="space-y-4">
        <ErrorState error={error || new Error('Role not found')} onRetry={refetch} />
        <div className="text-center">
          <Button asChild>
            <Link to="/admin/roles">Back to List</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/roles">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              {role.name}
            </h2>
            <p className="text-muted-foreground mt-1">Role Details</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button asChild>
            <Link to={`/admin/roles/${role.id}/edit`}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
          <Button
            variant="outline"
            onClick={handleToggleStatus}
            disabled={toggleStatusMutation.isLoading}
          >
            {role.active ? (
              <>
                <XCircle className="mr-2 h-4 w-4" />
                Deactivate
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Activate
              </>
            )}
          </Button>
          {!role.systemRole && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isLoading}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* System Role Warning */}
      {role.systemRole && (
        <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
              <AlertTriangle className="h-5 w-5" />
              <p className="text-sm font-medium">
                This is a system role. Name cannot be changed and it cannot be deleted.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Badge */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Label>Status:</Label>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  role.active
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                }`}
              >
                {role.active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              {role.systemRole ? (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  System Role
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                  Custom Role
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="permissions">Permissions ({permissions.length})</TabsTrigger>
          <TabsTrigger value="audit">Audit</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Role Information</CardTitle>
              <CardDescription>Basic role details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <CopyableId id={role.id || id || ''} label="Role ID" />
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={role.name} disabled={role.systemRole} />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <textarea
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  value={role.description || ''}
                  disabled
                />
              </div>

              <div className="space-y-2">
                <Label>Permission Count</Label>
                <Input value={role.permissions?.length || 0} disabled />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={role.active}
                  disabled
                  className="rounded border-gray-300"
                />
                <Label>Active</Label>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Permissions Tab */}
        <TabsContent value="permissions" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Permissions</CardTitle>
                  <CardDescription>Permissions assigned to this role</CardDescription>
                </div>
                <Button onClick={() => setAddPermissionModalOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Permission
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {permissions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No permissions assigned to this role
                </div>
              ) : (
                <div className="space-y-2">
                  {permissions.map((permission) => (
                    <div
                      key={permission.id}
                      className="flex items-center justify-between p-3 rounded-md border"
                    >
                      <div>
                        <div className="font-medium">{permission.name}</div>
                        {permission.description && (
                          <div className="text-sm text-muted-foreground">{permission.description}</div>
                        )}
                        <div className="text-xs text-muted-foreground mt-1">
                          {permission.resource && `Resource: ${permission.resource}`}
                          {permission.action && ` • Action: ${permission.action}`}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemovePermission(permission.name)}
                        disabled={removePermissionMutation.isLoading}
                      >
                        <Minus className="mr-2 h-4 w-4" />
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Audit Information</CardTitle>
              <CardDescription>Creation and modification history</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Created: </span>
                  <span>{formatDate(role.createdAt)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Updated: </span>
                  <span>{formatDate(role.updatedAt)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Version: </span>
                  <span>{role.version || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Created By: </span>
                  <span>{role.createdBy || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Last Modified By: </span>
                  <span>{role.lastModifiedBy || 'N/A'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Permission Modal */}
      <Dialog open={addPermissionModalOpen} onOpenChange={setAddPermissionModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Permission to Role</DialogTitle>
            <DialogDescription>
              Select a permission to add to this role
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={selectedPermissionName} onValueChange={setSelectedPermissionName}>
              <SelectTrigger>
                <SelectValue placeholder="Select a permission" />
              </SelectTrigger>
              <SelectContent>
                {availablePermissions.map((permission) => (
                  <SelectItem key={permission.id} value={permission.name}>
                    {permission.name}
                    {permission.description && ` - ${permission.description}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPermissionModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddPermission}
              disabled={!selectedPermissionName || addPermissionMutation.isLoading}
            >
              {addPermissionMutation.isLoading ? 'Adding...' : 'Add Permission'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleDetailPage;
